import axios from 'axios';

/**
 * Normalised API failure.
 *
 * Every rejection that leaves the API layer is an `ApiError`, so UI code never
 * has to branch on `AxiosError` internals or guess at FastAPI's two different
 * `detail` shapes (a string for `HTTPException`, an issue array for 422).
 */
export type ApiErrorKind =
  | 'network' // request never reached the server
  | 'timeout'
  | 'unauthorized' // 401 — token missing/expired; session is cleared
  | 'forbidden' // 403 — authenticated but out of scope
  | 'not_found' // 404
  | 'conflict' // 409
  | 'precondition' // 428 — mini-app login needs a phone
  | 'validation' // 422 — per-field issues
  | 'business' // 400 — domain rule rejected the request
  | 'rate_limited' // 429
  | 'server' // 5xx
  | 'unknown';

/** Field-level messages keyed by the form field name, ready for RHF. */
export type FieldErrors = Record<string, string>;

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number | null;
  readonly fieldErrors: FieldErrors;
  /** Raw payload, kept for logging and for features needing gateway details. */
  readonly payload: unknown;

  constructor(params: {
    message: string;
    kind: ApiErrorKind;
    status: number | null;
    fieldErrors?: FieldErrors;
    payload?: unknown;
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.kind = params.kind;
    this.status = params.status;
    this.fieldErrors = params.fieldErrors ?? {};
    this.payload = params.payload;
  }

  /** Retrying a request that failed this way could plausibly succeed. */
  get isRetryable(): boolean {
    return this.kind === 'network' || this.kind === 'timeout' || this.kind === 'server';
  }

  /** True when the failure is the user's input, not the system's state. */
  get isUserInput(): boolean {
    return this.kind === 'validation' || this.kind === 'business';
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

const KIND_BY_STATUS: Readonly<Record<number, ApiErrorKind>> = {
  400: 'business',
  401: 'unauthorized',
  403: 'forbidden',
  404: 'not_found',
  409: 'conflict',
  422: 'validation',
  428: 'precondition',
  429: 'rate_limited',
};

const DEFAULT_MESSAGES: Readonly<Record<ApiErrorKind, string>> = {
  network: 'ارتباط با سرور برقرار نشد. اتصال اینترنت خود را بررسی کنید.',
  timeout: 'پاسخی از سرور دریافت نشد. لطفاً دوباره تلاش کنید.',
  unauthorized: 'نشست شما منقضی شده است. دوباره وارد شوید.',
  forbidden: 'شما به این بخش دسترسی ندارید.',
  not_found: 'مورد درخواستی یافت نشد.',
  conflict: 'این عملیات با وضعیت فعلی سیستم تداخل دارد.',
  precondition: 'برای ادامه، تأیید شماره موبایل لازم است.',
  validation: 'اطلاعات واردشده معتبر نیست.',
  business: 'انجام این عملیات ممکن نیست.',
  rate_limited: 'تعداد درخواست‌ها زیاد است. کمی بعد دوباره تلاش کنید.',
  server: 'خطایی در سرور رخ داد. لطفاً بعداً تلاش کنید.',
  unknown: 'خطای پیش‌بینی‌نشده‌ای رخ داد.',
};

/** A single entry of FastAPI's 422 `detail` array. */
interface ValidationIssue {
  loc?: unknown[];
  msg?: string;
  type?: string;
}

function isValidationIssueArray(value: unknown): value is ValidationIssue[] {
  return (
    Array.isArray(value) &&
    value.every((item) => typeof item === 'object' && item !== null && 'msg' in item)
  );
}

/**
 * Map FastAPI's `[{loc: ["body", "phone"], msg}]` onto `{phone: msg}`.
 * `loc[0]` is the request part ("body"/"query"/"path") and is dropped so the
 * key matches the form field name.
 */
function toFieldErrors(issues: ValidationIssue[]): FieldErrors {
  const result: FieldErrors = {};
  for (const issue of issues) {
    const path = (issue.loc ?? []).filter(
      (segment): segment is string => typeof segment === 'string',
    );
    const field = path.length > 1 ? path.slice(1).join('.') : (path[0] ?? '_');
    if (issue.msg && !(field in result)) result[field] = issue.msg;
  }
  return result;
}

function extractDetail(data: unknown): { message: string | null; fieldErrors: FieldErrors } {
  if (typeof data === 'string' && data.trim()) return { message: data, fieldErrors: {} };
  if (typeof data !== 'object' || data === null) return { message: null, fieldErrors: {} };

  const detail = (data as { detail?: unknown }).detail;
  if (typeof detail === 'string') return { message: detail, fieldErrors: {} };
  if (isValidationIssueArray(detail)) {
    const fieldErrors = toFieldErrors(detail);
    return { message: Object.values(fieldErrors)[0] ?? null, fieldErrors };
  }
  const message = (data as { message?: unknown }).message;
  return { message: typeof message === 'string' ? message : null, fieldErrors: {} };
}

/**
 * Convert any thrown value into an `ApiError`. Called by the response
 * interceptor, so this is the single place error semantics are decided.
 */
export function normalizeError(error: unknown): ApiError {
  if (isApiError(error)) return error;

  if (axios.isAxiosError(error)) {
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      return new ApiError({
        message: DEFAULT_MESSAGES.timeout,
        kind: 'timeout',
        status: null,
        payload: error.code,
      });
    }
    if (!error.response) {
      return new ApiError({
        message: DEFAULT_MESSAGES.network,
        kind: 'network',
        status: null,
        payload: error.code,
      });
    }

    // Annotated rather than destructured: axios types the body as `any`, and
    // letting that leak past this boundary would defeat the whole error model.
    const status: number = error.response.status;
    const data: unknown = error.response.data;
    const kind: ApiErrorKind = KIND_BY_STATUS[status] ?? (status >= 500 ? 'server' : 'unknown');
    const { message, fieldErrors } = extractDetail(data);

    // A server's raw 5xx text is never useful to an end user; keep it in the
    // payload for logging and show the friendly copy instead.
    const showRaw = kind !== 'server' && kind !== 'unauthorized';

    return new ApiError({
      message: (showRaw && message) || DEFAULT_MESSAGES[kind],
      kind,
      status,
      fieldErrors,
      payload: data,
    });
  }

  return new ApiError({
    message: error instanceof Error ? error.message : DEFAULT_MESSAGES.unknown,
    kind: 'unknown',
    status: null,
    payload: error,
  });
}

/** Message suitable for a toast or inline alert, for any thrown value. */
export function toErrorMessage(error: unknown): string {
  return normalizeError(error).message;
}
